import React, { FC } from 'react';
import styled from '@emotion/styled';
import QRCode from 'react-qr-code';
import { useI18n } from '~Hooks';

/** Same width as the bong tickets, doubled by pixelRatio 2 at print time. */
const Wrapper = styled.div`
  background-color: white;
  color: black;
  font-family: sans-serif;
  width: 300px;
  box-sizing: border-box;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

/*
 * Note: no blanket `p { margin: 0 }` on the wrapper. A descendant selector beats the
 * per-element class, which silently wiped every margin-top below.
 */

/** Black block holding the title and the big order number. */
const NumberBlock = styled.div`
  background-color: black;
  color: white;
  width: 100%;
  padding: 6px 8px 8px;
`;

const Title = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Number = styled.p`
  margin: 0;
  font-size: 72px;
  font-weight: bold;
  line-height: 1;
`;

const QrWrapper = styled.div`
  margin-top: 24px;
  background-color: white;
`;

const ExtraMessage = styled.p`
  margin: 24px 0 0;
  font-size: 14px;
`;

/** Discreet, always the very last thing on the slip, no padding below it. */
const PoweredBy = styled.p`
  margin: 32px 0 0;
  font-size: 9px;
  color: #555;
  line-height: 1;
`;

interface Props {
  orderNumber: string;
  extraMessage?: string;
  /** Backoffice link rendered as a QR code; the whole QR section is skipped when unset. */
  qrCodeLink?: string | null;
}

/** Visual layout of the EPOS order-number slip, rendered off-screen and printed as an image. */
const OrderNumberSlipDesign: FC<Props> = ({ orderNumber, extraMessage, qrCodeLink }) => {
  const { i18n } = useI18n();

  return (
    <Wrapper>
      <NumberBlock>
        <Title>{i18n('SSK.YourOrderNumber')}</Title>
        <Number>{orderNumber}</Number>
      </NumberBlock>
      {qrCodeLink && (
        <QrWrapper>
          <QRCode value={qrCodeLink} size={96} />
        </QrWrapper>
      )}
      {extraMessage && <ExtraMessage>{extraMessage}</ExtraMessage>}
      <PoweredBy>{i18n('SSK.OrderNumber.PoweredBy')}</PoweredBy>
    </Wrapper>
  );
};

export default OrderNumberSlipDesign;
